import is from '@sindresorhus/is';

import type { SkillShort } from '~/shared/data-contract/skill';

import type { LearningOpportunity, LearningOpportunityCreate } from './data-contract';
import { mapSkillToLearningOpportunityPayload } from './helpers';

export type SidepanelMode = 'create' | 'edit' | 'view';

export type LearningOpportunityFormState = Omit<LearningOpportunityCreate, 'skills'> & {
    readonly ownerName?: string;
    readonly skills: readonly SkillShort[];
};

const emptyTranslation = (language: string): LearningOpportunityFormState['translations'][number] => ({
    description: '',
    language,
    title: '',
});

export const mergeTranslationsWithLanguages = (
    translations: LearningOpportunityFormState['translations'],
    languages: readonly string[],
): LearningOpportunityFormState['translations'] => {
    const existingByLanguage = new Map(translations.map((translation) => [translation.language, translation]));
    const mergedTranslations = languages.map(
        (language) => existingByLanguage.get(language) ?? emptyTranslation(language),
    );
    const knownLanguages = new Set(languages);
    const additionalTranslations = translations.filter((translation) => !knownLanguages.has(translation.language));

    return [...mergedTranslations, ...additionalTranslations];
};

export const mapOpportunityToFormState = (
    opportunity: LearningOpportunity,
    ownerName: string,
    languages: readonly string[],
): LearningOpportunityFormState => ({
    custom: opportunity.custom ?? {},
    learningLanguage: opportunity.learningLanguage,
    ownerName: opportunity.ownerName ?? ownerName,
    provider: opportunity.provider,
    skills: opportunity.skills ?? [],
    state: opportunity.state,
    translations: mergeTranslationsWithLanguages(opportunity.translations, languages),
    type: opportunity.type,
    url: opportunity.url ?? '',
});

export const createEmptyFormState = (
    ownerName: string,
    languages: readonly string[],
): LearningOpportunityFormState => ({
    custom: {},
    learningLanguage: undefined,
    ownerName,
    provider: undefined,
    skills: [],
    state: 'draft',
    translations: languages.map(emptyTranslation),
    type: 'course',
    url: '',
});

export const getLocalizedTranslationIndex = (state: LearningOpportunityFormState, languageCode: string): number => {
    const index = state.translations.findIndex((translation) => translation.language === languageCode);

    return index >= 0 ? index : 0;
};

export const submitFormPayload = (
    value: LearningOpportunityFormState,
    onSubmit: (value: LearningOpportunityCreate) => void,
): void => {
    const { ownerName: _ownerName, ...payload } = value;
    onSubmit({
        ...payload,
        skills: payload.skills.map(mapSkillToLearningOpportunityPayload),
        translations: payload.translations.filter(
            (translation) => is.nonEmptyString(translation.title) || is.nonEmptyString(translation.description),
        ),
    });
};
